import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, ASSET_URL } from '../../config';

const API = `${API_URL}/admin`;

const CATEGORIES = ['phones', 'glass', 'speakers', 'playstation'];
const BADGES     = ['', 'New', 'Hot', 'Sale', 'Best Seller', 'Premium', 'Limited', 'GOTY'];

const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('matrix_token')}` } });

const EMPTY = { name:'', category:'phones', price:'', originalPrice:'', imageUrl:'', badge:'', description:'', stock:'' };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab]           = useState('products');
  const [products, setProducts] = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [modal, setModal]       = useState(null); // null | 'add' | 'edit'
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState('');

  const admin = localStorage.getItem('matrix_admin') || 'Admin';

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const logout = () => {
    localStorage.removeItem('matrix_token');
    localStorage.removeItem('matrix_admin');
    navigate('/admin/login');
  };

  const checkAuth = useCallback(() => {
    if (!localStorage.getItem('matrix_token')) navigate('/admin/login');
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search)    params.set('search', search);
      if (catFilter) params.set('category', catFilter);
      const { data } = await axios.get(`${API}/products?${params}`, authH());
      setProducts(data.data);
    } catch { logout(); }
    finally { setLoading(false); }
  }, [search, catFilter]);  // eslint-disable-line

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/stats`, authH());
      setStats(data.data);
    } catch {}
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { if (tab === 'stats') loadStats(); }, [tab, loadStats]);

  const openAdd  = () => { setForm(EMPTY); setEditing(null); setImageFile(null); setModal('add'); };
  const openEdit = (p) => {
    setForm({ name: p.name, category: p.category, price: p.price, originalPrice: p.originalPrice || '',
      imageUrl: p.image?.startsWith('http') ? p.image : '', badge: p.badge || '',
      description: p.description || '', stock: p.stock });
    setEditing(p); setImageFile(null); setModal('edit');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (imageFile) fd.append('image', imageFile);

      if (modal === 'edit') {
        await axios.put(`${API}/products/${editing.id}`, fd, { ...authH(), headers: { ...authH().headers, 'Content-Type': 'multipart/form-data' } });
        showToast('Product updated ✓');
      } else {
        await axios.post(`${API}/products`, fd, { ...authH(), headers: { ...authH().headers, 'Content-Type': 'multipart/form-data' } });
        showToast('Product added ✓');
      }
      setModal(null);
      loadProducts();
    } catch { showToast('Error saving product'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API}/products/${id}`, authH());
      showToast('Deleted ✓');
      loadProducts();
    } catch { showToast('Error deleting'); }
  };

  const handleStockChange = async (id, stock) => {
    try {
      await axios.patch(`${API}/products/${id}/stock`, { stock: parseInt(stock) }, authH());
      setProducts(ps => ps.map(p => p.id === id ? { ...p, stock: parseInt(stock) } : p));
    } catch {}
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar__logo">
          <img src="/logo-matrix.png" alt="Matrix" />
        </div>
        <nav className="admin-sidebar__nav">
          {[
            { id: 'products', icon: '📦', label: 'Products' },
            { id: 'stats',    icon: '📊', label: 'Dashboard' },
          ].map(item => (
            <button
              key={item.id}
              className={`admin-nav-btn ${tab === item.id ? 'admin-nav-btn--active' : ''}`}
              onClick={() => setTab(item.id)}
            >
              <span>{item.icon}</span> {item.label}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar__footer">
          <div className="admin-user">
            <div className="admin-avatar">{admin[0].toUpperCase()}</div>
            <div><strong>{admin}</strong><small>Administrator</small></div>
          </div>
          <button className="admin-logout" onClick={logout}>Sign out</button>
          <a href="/" className="admin-store-link" target="_blank" rel="noreferrer">← View Store</a>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        {/* Toast */}
        {toast && <div className="admin-toast">{toast}</div>}

        {/* PRODUCTS TAB */}
        {tab === 'products' && (
          <div className="admin-tab">
            <div className="admin-tab__header">
              <div>
                <h1>Products</h1>
                <p>{products.length} total products</p>
              </div>
              <button className="btn btn--primary" onClick={openAdd}>+ Add Product</button>
            </div>

            <div className="admin-toolbar">
              <input
                className="admin-search"
                placeholder="Search products..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <select className="sort-select" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
                <option value="">All Categories</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="admin-loading">Loading...</div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Image</th><th>Name</th><th>Category</th>
                      <th>Price (TND)</th><th>Stock</th><th>Badge</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(p => (
                      <tr key={p.id} className={p.stock === 0 ? 'row--out' : p.stock < 5 ? 'row--low' : ''}>
                        <td>
                          <img src={p.image?.startsWith('/uploads') ? `${ASSET_URL}${p.image}` : p.image}
                            alt={p.name} className="admin-product-img" />
                        </td>
                        <td><strong>{p.name}</strong></td>
                        <td><span className="cat-chip">{p.category}</span></td>
                        <td><strong className="price-red">{Number(p.price).toLocaleString()} TND</strong>
                          {Number.isFinite(Number(p.originalPrice)) && Number(p.originalPrice) > 0 && <small className="admin-orig"> /{Number(p.originalPrice).toLocaleString()}</small>}
                        </td>
                        <td>
                          <input
                            type="number" min="0"
                            value={p.stock}
                            onChange={e => handleStockChange(p.id, e.target.value)}
                            className="stock-input"
                          />
                        </td>
                        <td>{p.badge ? <span className="badge-chip">{p.badge}</span> : '—'}</td>
                        <td className="admin-actions">
                          <button className="admin-btn admin-btn--edit" onClick={() => openEdit(p)}>Edit</button>
                          <button className="admin-btn admin-btn--del"  onClick={() => handleDelete(p.id, p.name)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* STATS TAB */}
        {tab === 'stats' && stats && (
          <div className="admin-tab">
            <div className="admin-tab__header">
              <div><h1>Dashboard</h1><p>Overview of your store</p></div>
            </div>
            <div className="stats-grid">
              {[
                { label: 'Total Products', value: stats.total, icon: '📦' },
                { label: 'Total Stock',    value: stats.totalStock, icon: '🏭' },
                { label: 'Low Stock',      value: stats.lowStock, icon: '⚠️' },
                { label: 'Out of Stock',   value: stats.outOfStock, icon: '🚫' },
                { label: 'Avg Price',      value: `${Math.round(stats.avgPrice).toLocaleString()} TND`, icon: '💰' },
                { label: 'Catalog Value',  value: `${Math.round(stats.totalValue).toLocaleString()} TND`, icon: '💎' },
              ].map(s => (
                <div key={s.label} className="stat-card">
                  <span className="stat-card__icon">{s.icon}</span>
                  <div><strong>{s.value}</strong><p>{s.label}</p></div>
                </div>
              ))}
            </div>

            <div className="stats-section">
              <h2>By Category</h2>
              <div className="cat-stats">
                {stats.byCategory.map(c => (
                  <div key={c.category} className="cat-stat-card">
                    <h3>{c.category}</h3>
                    <p>{c.count} products · {c.totalStock} in stock</p>
                    <p className="price-red">{Math.round(c.avgPrice).toLocaleString()} TND avg</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="stats-section">
              <h2>Low Stock Alert</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th></tr></thead>
                  <tbody>
                    {stats.lowStockProducts.map(p => (
                      <tr key={p.id} className={p.stock === 0 ? 'row--out' : 'row--low'}>
                        <td><strong>{p.name}</strong></td>
                        <td>{p.category}</td>
                        <td>{Number(p.price).toLocaleString()} TND</td>
                        <td><span className={`stock-badge ${p.stock === 0 ? 'stock-badge--out' : 'stock-badge--low'}`}>{p.stock}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal Add/Edit */}
      {modal && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h2>{modal === 'edit' ? 'Edit Product' : 'Add Product'}</h2>
              <button className="admin-modal__close" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleSave} className="admin-modal__form">
              <div className="form-row">
                <div className="form-field">
                  <label>Name *</label>
                  <input value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} required />
                </div>
                <div className="form-field">
                  <label>Category *</label>
                  <select value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Price (TND) *</label>
                  <input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm(f=>({...f,price:e.target.value}))} required />
                </div>
                <div className="form-field">
                  <label>Original Price (TND)</label>
                  <input type="number" min="0" step="0.01" value={form.originalPrice} onChange={e => setForm(f=>({...f,originalPrice:e.target.value}))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Stock</label>
                  <input type="number" min="0" value={form.stock} onChange={e => setForm(f=>({...f,stock:e.target.value}))} />
                </div>
                <div className="form-field">
                  <label>Badge</label>
                  <select value={form.badge} onChange={e => setForm(f=>({...f,badge:e.target.value}))}>
                    {BADGES.map(b => <option key={b} value={b}>{b || 'None'}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea rows="2" value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} />
              </div>
              <div className="form-field">
                <label>Image URL</label>
                <input placeholder="https://..." value={form.imageUrl} onChange={e => setForm(f=>({...f,imageUrl:e.target.value}))} />
              </div>
              <div className="form-field">
                <label>Or upload image</label>
                <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} />
                {modal === 'edit' && editing?.image && !imageFile && (
                  <img
                    src={editing.image.startsWith('/uploads') ? `${ASSET_URL}${editing.image}` : editing.image}
                    alt="current" className="admin-img-preview"
                  />
                )}
              </div>
              <div className="admin-modal__footer">
                <button type="button" className="btn btn--ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn--primary" disabled={saving}>
                  {saving ? 'Saving...' : modal === 'edit' ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
